import React, {useState} from 'react';

import * as AuditActions from 'audit/AuditActions';
import Breadcrumbs from 'nav/Breadcrumbs';
import clampEntryName from 'notebook/entry-templates/clampEntryName';
import {MoveToTemplateCollectionModal} from 'notebook/entry-templates/collections/MoveToTemplateCollectionModal';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import EditableTextField from 'ui-library/inputs/EditableTextField';
import OverflowMenu, {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import Modal from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';

export function RenameModal({
  initialName,
  onRequestSubmit,
  onRequestClose,
}: {
  initialName: string;
  onRequestSubmit: (name: string) => void;
  onRequestClose: () => void;
}): JSX.Element {
  const [name, setName] = useState(initialName);
  return (
    <Modal
      title="Rename form"
      large={false}
      buttons={[
        {
          text: 'Save',
          submit: true,
          disabled: !name.length,
        },
      ]}
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        onRequestSubmit(name);
      }}
    >
      <FormGroup>
        <Label>Name</Label>
        <input
          type="text"
          value={name}
          onChange={(e) => {
            setName(clampEntryName(e.target.value));
          }}
          autoFocus={true}
        />
      </FormGroup>
    </Modal>
  );
}

export interface FormDefinitionPageHeaderProps {
  id?: string;
  name: string;
  onUpdateName: (name: string) => void;
  templateCollectionId: string;
  onUpdateTemplateCollectionId: (templateCollectionId: string) => void;
  saveButtonText: string;
  isSubmittable: boolean;
  onClickSave: () => void;
  archivePurpose?: string;
  onArchive?: () => void;
  onUnarchive?: () => void;
}

/**
 * Header for the form definition page with an editable name and save button.
 */
export default function FormDefinitionPageHeader(props: FormDefinitionPageHeaderProps): JSX.Element {
  const menuOptions: Array<OverflowMenuOption> = [];

  const isArchived = Boolean(props.archivePurpose);

  // Only show rename and move buttons for non-archived forms.
  if (!isArchived) {
    menuOptions.push(
      {
        icon: 'edit',
        contents: <span>Rename</span>,
        onSelect: () => {
          runModal(RenameModal, {
            initialName: props.name,
            onRequestSubmit: props.onUpdateName,
          });
        },
      },
      {
        icon: 'move',
        contents: <span>Move</span>,
        onSelect: () => {
          runModal(MoveToTemplateCollectionModal, {
            initialTemplateCollectionId: props.templateCollectionId,
            onRequestSubmit: props.onUpdateTemplateCollectionId,
            title: 'Move form',
          });
        },
      }
    );
  }
  // Only show (un)archive and audit log buttons for existing form definitions.
  if (props.id) {
    if (isArchived) {
      menuOptions.push({
        icon: 'unarchive',
        contents: <span>Unarchive</span>,
        onSelect: props.onUnarchive,
      });
    } else {
      menuOptions.push({
        icon: 'archive',
        contents: <span>Archive</span>,
        onSelect: props.onArchive,
      });
    }

    menuOptions.push({
      icon: 'audit-trail',
      contents: <span>Export audit logs</span>,
      onSelect: () => {
        if (props.id) {
          AuditActions.exportAuditLogs([props.id]);
        }
      },
    });
  }

  let disabledTooltip;
  if (isArchived) {
    disabledTooltip = 'Archived form cannot be edited';
  } else if (!props.isSubmittable) {
    disabledTooltip = 'No changes to save';
  }

  return (
    <span className="u-lr-split">
      <Breadcrumbs
        crumbs={[
          <span key="form-template-name" className="u-display-inline-block u-small-padding-left">
            {isArchived ? (
              <span className="u-faded">
                {props.name} ({props.archivePurpose})
              </span>
            ) : (
              <EditableTextField
                requestChange={props.onUpdateName}
                value={props.name}
                disableEditsWithTooltip={isArchived ? 'Archived form cannot be renamed' : null}
              />
            )}
          </span>,
        ]}
        className="breadcrumbs--large"
      />
      <div className="u-vertical-center-items">
        <Tooltip text={disabledTooltip} placement={FloatingPlacement.BOTTOM} isDisabled={!disabledTooltip}>
          <Button
            disabled={Boolean(disabledTooltip)}
            buttonStyle={ButtonStyle.SECONDARY}
            text={props.saveButtonText}
            size={ButtonSize.SMALL}
            onClick={props.onClickSave}
          />
        </Tooltip>
        <OverflowMenu
          buttonProps={{size: ButtonSize.SMALL}}
          menuProps={{dropLeft: true}}
          options={menuOptions}
          tooltipPlacement={FloatingPlacement.LEFT}
        />
      </div>
    </span>
  );
}
